import React, { useState } from 'react';
import Button from 'components/customDesign/Button';
import DialogContent from 'components/customDesign/DialogContent';
import { ReactComponent as SmileIcon } from 'assets/images/success.svg';
import { Formik } from 'formik';
import Input from 'components/customDesign/Input';
import erlClient from 'services/clients';
import memoize from 'lodash/memoize';
import ListInfoItem from 'components/customDesign/ListInfoItem';
import fonts from 'styles/typography.module.css';
import { Dialog, FormControl, FormHelperText } from '@mui/material';
import NeedValidation from './NeedValidation';

const validateScacExist = memoize((scac) =>
  erlClient.get(`/core/api/v1/auth/company/scac/${scac}`)
);

const NeedJoinCompany: React.FC = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [secondComer, setSecondComer] = useState(false);
  const [needValidation, setNeedValidation] = useState(false);

  const close = () => setIsOpen(false);
  const validate = async (values: { scac?: string }) => {
    const errors: { scac?: string } = {};
    setSecondComer(false);
    if (!values.scac) errors.scac = 'Required';
    if (values.scac && !errors.scac) {
      try {
        await validateScacExist(values.scac);
      } catch (e) {
        setSecondComer(true);
      }
    }
    // eslint-disable-next-line
    throw errors;
  };

  const submit = (
    values: { scac: string },
    {
      setSubmitting,
      setErrors,
    }: // eslint-disable-next-line @typescript-eslint/no-explicit-any
    { setSubmitting: (value: boolean) => void; setErrors: (value: any) => void }
  ) =>
    erlClient
      .post('/core/api/v1/auth/company/join', { scac: values.scac })
      .then(() => {
        setSubmitting(false);

        if (!secondComer) {
          close();
          window.location.reload();
        } else setNeedValidation(true);
      })
      .catch((err) => {
        setErrors(err.response.data);
        setSubmitting(false);
      });

  return (
    <Dialog
      open={isOpen}
      classes={{ paper: `p-8 w-[400px] flex flex-col items-center gap-2 ${fonts.font}` }}
    >
      <Formik initialValues={{ scac: '' }} validate={validate} onSubmit={submit}>
        {({ values, handleChange, errors, touched, handleBlur, isSubmitting, handleSubmit }) =>
          needValidation ? (
            <>
              <SmileIcon width={36} height={36} className="" />
              <h1 className="text-center font-bold">Special offer!</h1>
              <NeedValidation scac={values.scac} />
            </>
          ) : (
            <form onSubmit={handleSubmit} className="flex flex-col">
              <DialogContent
                TopIcon={SmileIcon}
                title="Special offer!"
                subtitle={
                  <div>
                    <span>
                      To keep using the platform, unlock a <br />
                      <span className="underline">Premium</span> account for
                      <span className="text-orange-800 font-bold"> 15 days free</span>!
                    </span>
                  </div>
                }
                classes={{ content: 'space-y-8' }}
                callToAction={
                  <>
                    <Button
                      type="submit"
                      classes={{ root: 'bg-orange-800' }}
                      isLoading={isSubmitting}
                      disabled={isSubmitting}
                    >
                      Let&apos;s go!
                    </Button>
                    <div>
                      <h5 className="underline text-gray-400 text-center">
                        <a href="mailto:support@bluecargo.io">Contact us</a>
                      </h5>
                    </div>
                  </>
                }
              >
                <div className="space-y-3 pl-8">
                  <ListInfoItem
                    primary="No credit card required"
                    secondary="We'll remind you few days before your trial ends"
                  />
                  <ListInfoItem
                    primary="Free"
                    secondary="You won't be charged at all! Enter a credit card after your 15 days free!"
                  />
                  <ListInfoItem primary="Non-binding" secondary="No commitment, cancel anytime" />
                </div>
                <FormControl
                  required
                  fullWidth
                  error={!!(errors.scac && touched.scac && touched.scac)}
                >
                  <Input
                    placeholder="Enter your company SCAC *"
                    onBlur={handleBlur}
                    name="scac"
                    value={values.scac}
                    onChange={(event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) =>
                      handleChange({
                        target: {
                          name: event.target.name,
                          value: event.target.value.toUpperCase(),
                        },
                      })
                    }
                  />
                  <FormHelperText>
                    {errors.scac && touched.scac && errors.scac}
                    {secondComer && 'This company already has an account'}
                  </FormHelperText>
                </FormControl>
              </DialogContent>
            </form>
          )
        }
      </Formik>
    </Dialog>
  );
};

export default NeedJoinCompany;
